import {
  ACTIVITY_FEED_PARAMS,
  ADDED_NOTE,
  BETA_FEATURES,
  DATA_STATUS,
  HTTP_STATUSES,
  IS_EDITING_FULL_NAME_FLAG,
  LOADING_SCREEN_REFETCH_TEXT,
  PROPERTY_DETAILS_TAB_NAMES,
} from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as userSelectors from '@/user/user-selectors';
import { FetchPropertyDetailsError } from '@/utils/sentry-error-classes';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import { toaster } from '@/utils/toaster';
import * as Sentry from '@sentry/browser';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { all, fork, put, select, takeLatest } from 'redux-saga/effects';
import { getLicenseExpirationDateString } from '../license/utils/license-utils';
import * as dataActions from './data-actions';
import {
  USER_UPDATE_COMPLAINT_STATUS,
  updateComplaintStatus,
} from './data-actions';
import * as dataSelectors from './data-selectors';
import {
  complaintsData,
  isBetaFeatureEnabled,
  licenseEditButtonConfig,
  strComplaintsAvailable,
} from './data-selectors';

function handleException(err, userMessage = 'An error has occurred') {
  console.error('Caught error:', err); // eslint-disable-line no-console
  Sentry.captureException(err);
  toaster.error(userMessage);
}

/**
 * Saga to fetch one of the potentially multiple endpoints required for a tab of the
 * property details screen.
 * @param {string} propertyDeckardID Must be encoded as "retrieve" method of StrOracleApiSender will not perform the encoding.
 * @param {string} path Part of the STROracle API endpoint to fetch, relative to the
 * API path for the current city (e.g. the full API endpoint for Imperial Beach would be
 * `/dashboard/v1/imperialbeachdummy/${path}/${deckardid}`)
 * @param {function} dataAction Action creator of the action to be dispatched with
 * the results from the endpoint. The action's payload will be of the type
 * `{ data: TAPIResult | DATA_STATUS[keyof DATA_STATUS] }`, i.e. the data field of the
 * payload will either contain the result object returned by the endpoint, or it will
 * contain one of the DATA_STATUS strings if response has not yet been received or
 * if there was an exception.
 * @param {function} extractResult Optional function that takes in the object returned
 * by the API endpoint and transforms it before putting the result into the `dataAction`
 * action payload. Useful for "unwrapping" nested data structures coming from the API.
 * @returns {Generator<SimpleEffect<"PUT", PutEffectDescriptor<*>>|Promise<*>, void, *>}
 */
function* fetchSingleGenericDetailsEndpoint({
  propertyDeckardID,
  path,
  dataAction,
  queryParams,
  headers = {},
  extractResult = (data) => data,
}) {
  yield put(dataAction({ data: DATA_STATUS.inProgress }));
  let response;
  try {
    response = yield strOracleApiSender.retrieve(path, propertyDeckardID, {
      validateStatus: false,
      queryParams,
      headers,
    });

    if (response) {
      const { data } = response;
      // if status is 404, data is { Code: "NotFoundError" }
      // TODO: Remove after https://deckardtech.atlassian.net/browse/DECK-4281

      if (response.status === 404) data.deckardid = propertyDeckardID;
      yield put(dataAction({ data: extractResult(data) }));
    }
  } catch (err) {
    const contexts = { ...(response?.data && { data: response.data }) };
    Sentry.captureException(new FetchPropertyDetailsError(), { contexts });
    yield put(dataAction({ data: DATA_STATUS.dataError }));
  }
}

/**
 *  @param {string} propertyDeckardID Must be encoded as "retrieve" method of StrOracleApiSender will not perform the encoding.
 *  @param {string} tab The active Property Details tab.
 */
function* fetchGenericDetails(propertyDeckardID, tab) {
  const isComplaintModuleAvailable = yield select(strComplaintsAvailable);

  const userRights = yield select(userSelectors.userRights);
  const canViewLicenses = Boolean(userRights?.viewLicense);

  const requestConfigs = {
    overview: [
      {
        path: '/property/',
        dataAction: dataActions.serverReturnedPropertyData,
        queryParams: { include_reviews: true },
      },
      isComplaintModuleAvailable && {
        path: '/property/complaints/',
        dataAction: dataActions.serverReturnedPropertyComplaintsData,
        extractResult: (data) => data.complaints,
      },
    ].filter(Boolean),
    activity: [
      {
        path: '/property/activity/',
        dataAction: dataActions.serverReturnedActivityData,
        queryParams: {
          ...(isComplaintModuleAvailable && {
            [ACTIVITY_FEED_PARAMS.INCLUDE_COMPLAINTS]: true,
          }),
        },
      },
    ],
    license: canViewLicenses
      ? [
          {
            path: '/property/licenses/',
            dataAction: dataActions.serverReturnedLicensesData,
          },
        ]
      : [],
    history: [
      {
        path: '/property/',
        dataAction: dataActions.serverReturnedPropertyData,
        queryParams: { include_reviews: true },
      },
      canViewLicenses && {
        path: '/property/licenses/',
        dataAction: dataActions.serverReturnedLicensesData,
        queryParams: { include_license_tot_payments: true },
      },
    ].filter(Boolean),
    propertyOverviewTaxTab: [
      {
        path: '/property/tot/',
        dataAction: dataActions.serverReturnedTOTPaymentsData,
        extractResult: (data) => data.licenses,
      },
    ],
  };

  yield all(
    (requestConfigs[tab] || []).map(
      ({ path, dataAction, extractResult, queryParams }) =>
        fetchSingleGenericDetailsEndpoint({
          propertyDeckardID,
          path,
          dataAction,
          extractResult,
          queryParams,
        })
    )
  );
}

/**
 * This saga fetches all the data for all tabs in the property details page.
 * It should only fetch new data if the deckardid passed by the caller
 * is different than the deckardid of the stored property data
 *
 * @param {string} action.payload.propertyDeckardID Must be encoded as "retrieve" method of StrOracleApiSender will not perform the encoding.
 */
function* fetchPropertyDetailsDataSaga(action) {
  const { propertyDeckardID } = action.payload;

  const tabs = yield select(dataSelectors.propertyDetailsTabs); // tab configuration from /stats
  const currentPropertyData = yield select(dataSelectors.propertyData);

  const propertyOverviewTaxTab = yield select(
    dataSelectors.betaFeatureConfigValue(BETA_FEATURES.propertyOverviewTaxTab)
  );

  // Prevents re-fetch the same data
  if (currentPropertyData?.deckardid === propertyDeckardID) return;

  yield fork(
    fetchGenericDetails,
    propertyDeckardID,
    PROPERTY_DETAILS_TAB_NAMES.overview
  );

  if (tabs?.activity?.enabled) {
    yield fork(
      fetchGenericDetails,
      propertyDeckardID,
      PROPERTY_DETAILS_TAB_NAMES.activity
    );
  }

  if (tabs?.license?.enabled) {
    yield fork(
      fetchGenericDetails,
      propertyDeckardID,
      PROPERTY_DETAILS_TAB_NAMES.license
    );
  }

  if (tabs?.history?.enabled) {
    yield fork(
      fetchGenericDetails,
      propertyDeckardID,
      PROPERTY_DETAILS_TAB_NAMES.history
    );
  }

  if (propertyOverviewTaxTab?.enabled === true) {
    yield fork(
      fetchGenericDetails,
      propertyDeckardID,
      PROPERTY_DETAILS_TAB_NAMES.propertyOverviewTaxTab
    );
  }

  yield fork(
    fetchGenericDetails,
    propertyDeckardID,
    PROPERTY_DETAILS_TAB_NAMES.inspections
  );
}

export function* fetchPropertyDetailsDataWatcher() {
  yield takeLatest(
    dataActions.FETCH_PROPERTY_DETAILS_DATA,
    fetchPropertyDetailsDataSaga
  );
}

function* userEditedNoteSaga(action) {
  const { newMessage, noteId, index } = action.payload;

  const activityData = yield select(dataSelectors.activityData);

  // Get current value. This will be used to revert update if update request fails
  const preEditValue = activityData.getIn([index, 'value']);
  const newValue = { ...preEditValue, message: newMessage };

  // optimistic update of notes hoping all will go well
  yield put(dataActions.updateNote({ index, value: newValue }));

  // send request
  try {
    const { data } = yield strOracleApiSender.post(`/update_note/${noteId}`, {
      message: newMessage,
    });
    yield put(dataActions.updateNote({ index, value: data?.value }));
  } catch (err) {
    /*
     * On error:
     *   - Let the user know something went wrong
     *   - Revert the note to its original value
     */
    console.error('Caught error:', err); // eslint-disable-line no-console
    // TODO: Bing has to tell us what to put here
    toaster.error('Error updating note');
    // on error or fail, revert
    yield put(dataActions.updateNote({ index, value: preEditValue }));

    // Send error to Sentry
    Sentry.captureException(err, {
      contexts: {
        devMsg: 'Error caught while editing note',
        noteId,
        noteValue: newMessage,
        caughtError: err,
      },
    });
  }
}

export function* userEditedNoteWatcher() {
  yield takeLatest(dataActions.USER_EDITED_NOTE, userEditedNoteSaga);
}

/**
 * @param {URLSearchParams | undefined} action.payload.licenseTabParam Refer to renders of "ViolationSummaryNotes"
 * @param {String} action.payload.message The text content of the note
 * @param {String} action.payload.propertyDeckardID Must be encoded as "submit" method of StrOracleApiSender will not perform the encoding.
 */
function* userAddedNoteSaga(action) {
  const { message, propertyDeckardID, licenseTabParam } = action.payload;

  const name = yield select(userSelectors.userFullName);
  const email = yield select(userSelectors.username);

  // we will use this to identify the note in case we need to delete it
  const currNoteTimestamp = Date.now();
  yield put(
    dataActions.addNote({
      message,
      currNoteTimestamp,
      author: { name, email },
      event: ADDED_NOTE, // Not really needed, but adding 'event' message to keep things consistent
    })
  );

  yield put(
    dataActions.setNoteAddStatus({ inFlight: true, addedNoteId: null })
  );

  // send request
  try {
    const { data: note } = yield strOracleApiSender.submit(
      '/add_note',
      propertyDeckardID,
      licenseTabParam,
      { message }
    );

    // on success, update note with incoming information (contains new note id)
    yield put(dataActions.updateTempNote({ currNoteTimestamp, note }));
    yield put(
      dataActions.setNoteAddStatus({ inFlight: false, addedNoteId: note.id })
    );
  } catch (err) {
    /**
     * On error:
     *   - Let the user know something went wrong
     *   - Revert the note to its original value
     */
    console.error('Caught error:', err); // eslint-disable-line no-console
    // TODO: Bing has to tell us what to put here
    if (err.response?.status === HTTP_STATUSES.FORBIDDEN) {
      toaster.error('Your user account is not authorized to add notes.');
    } else {
      toaster.error('Error adding note');
    }
    // on error or fail, revert
    yield put(dataActions.serverFailedToAddNote({ currNoteTimestamp }));
    yield put(
      dataActions.setNoteAddStatus({ inFlight: false, addedNoteId: null })
    );

    // Send error to Sentry
    Sentry.captureException(err, {
      contexts: {
        devMsg: 'Error caught while adding note',
        propertyDeckardID,
        message,
        caughtError: err,
      },
    });
  }
}

export function* userAddedNoteWatcher() {
  yield takeLatest(dataActions.USER_ADDED_NOTE, userAddedNoteSaga);
}

const PERSON_NON_EQUALITY_KEYS = ['misc'];
function personEqual(p1, p2) {
  return isEqual(
    omit(p1, PERSON_NON_EQUALITY_KEYS),
    omit(p2, PERSON_NON_EQUALITY_KEYS)
  );
}

function copyPersonSupplementalKeys(dest, src) {
  return {
    ...dest,
    ...pick(src || {}, PERSON_NON_EQUALITY_KEYS),
  };
}

function* userUpdateLicenseSaga({
  payload: { originalLicense, license, index, updatedMiscFields },
}) {
  const url = `/update_license/${license?.licenseDeckardid}`;
  const registrant = license?.people?.registrant;
  const registrantType = license?.certificateInfo?.registrantType;

  const payload = {};

  const people = {};

  const allowEditingOwner = yield select(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonOwner)
  );

  const isAdminUpdateFlowEnabled = yield select(
    isBetaFeatureEnabled(BETA_FEATURES.adminUpdateFlow)
  );

  const shouldEditPeople = !isAdminUpdateFlowEnabled;

  const owner = license?.people?.owner;
  const originalOwner = originalLicense?.people?.owner;
  if (owner && originalOwner && allowEditingOwner) {
    people.owner = owner;
  }

  const originalRegistrant = originalLicense.people.registrant;

  if (registrant && originalRegistrant) {
    // Disallow editing names for now
    people.registrant = {
      ...registrant,
      name: originalRegistrant.name,
      firstName: originalRegistrant.firstName,
      lastName: originalRegistrant.lastName,
    };

    const allowEditingName = yield select(
      isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonRegistrantName)
    );

    const allowEditingFullName = yield select(
      isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonRegistrantFullName)
    );

    if (allowEditingName) {
      const editFullName =
        allowEditingFullName && people.registrant[IS_EDITING_FULL_NAME_FLAG];
      const editFirstLastName = !people.registrant[IS_EDITING_FULL_NAME_FLAG];

      let nameFieldChanges = {};
      if (editFullName) {
        nameFieldChanges = {
          name: registrant.name,
        };
      } else if (editFirstLastName) {
        nameFieldChanges = {
          firstName: registrant.firstName,
          lastName: registrant.lastName,
        };
      }

      people.registrant = {
        ...people.registrant,
        ...nameFieldChanges,
      };

      if (editFullName) {
        delete people.registrant.firstName;
        delete people.registrant.lastName;
      } else if (editFirstLastName) {
        delete people.registrant.name;
      }
    }

    delete people.registrant[IS_EDITING_FULL_NAME_FLAG];

    // If the registrant is also the owner/propertyManager/etc,
    // update the corresponding owner/propertyManager person as well
    // as the registrant person

    if (originalRegistrant) {
      [registrantType, ...Object.keys(originalLicense.people)].forEach(
        (personType) => {
          if (personType === 'registrant') return;

          const originalPersonValue = originalLicense.people[personType];
          if (!originalPersonValue) return;

          if (Array.isArray(originalPersonValue)) {
            const indexToReplace = originalPersonValue.findIndex(
              (individualOriginalPerson) =>
                personEqual(individualOriginalPerson, originalRegistrant)
            );

            if (indexToReplace >= 0) {
              const newPeople = people[personType] || [...originalPersonValue];
              newPeople[indexToReplace] = copyPersonSupplementalKeys(
                people.registrant,
                newPeople[indexToReplace]
              );

              people[personType] = newPeople;
            }
          } else if (
            personEqual(originalPersonValue, originalRegistrant) ||
            (Boolean(registrantType) && personType === registrantType)
          ) {
            people[personType] = copyPersonSupplementalKeys(
              people.registrant,
              originalPersonValue
            );
          }
        }
      );
    }
  }

  if (Object.keys(people).length && shouldEditPeople) {
    payload.people = people;
  }

  const allowEditingExpiryDate = yield select(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonExpirationDate)
  );

  if (allowEditingExpiryDate) {
    const expirationDate = getLicenseExpirationDateString(license);
    const originalExpirationDate =
      getLicenseExpirationDateString(originalLicense);
    if (expirationDate !== originalExpirationDate && expirationDate) {
      payload.expirationDate = expirationDate;
    }
  }

  const config = yield select(licenseEditButtonConfig);

  if (config?.editableFields?.length) {
    payload.misc = updatedMiscFields;
  }

  yield put(
    dataActions.updateLicense({
      index,
      inFlight: true,
      partialLicense: payload,
    })
  );

  try {
    const { data: response } = yield strOracleApiSender.post(url, payload);
    yield put(
      dataActions.serverReturnedUpdatedLicense({ license: response, index })
    );

    toaster.success('License updates saved');
  } catch (err) {
    handleException(err, 'Error updating license');
    yield put(
      dataActions.updateLicense({
        index,
        inFlight: false,
        replaceLicense: originalLicense,
      })
    );

    /* TODO: Remove this once the API does not commit changes when
        it fails due to missing email templates. This change is here for now
        to prevent misleading the user into thinking that no changes have occurred
     */
    const propertyDeckardId = originalLicense?.propertyDeckardid;
    if (propertyDeckardId) {
      yield fork(
        fetchGenericDetails,
        propertyDeckardId,
        PROPERTY_DETAILS_TAB_NAMES.license
      );
    }
  }
}

export function* userUpdateLicenseWatcher() {
  yield takeLatest(dataActions.USER_UPDATE_LICENSE, userUpdateLicenseSaga);
}

function* userUpdateLicenseStatusSaga(action) {
  const { oldStatus, status, index, licenseDeckardid } = action.payload;

  // Setting inFlight to true on existing license object
  yield put(dataActions.updateLicenseStatus({ status, index, inFlight: true }));

  try {
    const { data } = yield strOracleApiSender.post(
      `/update_license_status/${licenseDeckardid}`,
      {
        status,
      }
    );

    // Not setting inFlight to false bc entire object rewritten
    yield put(
      dataActions.serverReturnedUpdatedLicense({ license: data, index })
    );
  } catch (err) {
    console.error('Caught error:', { err }); // eslint-disable-line no-console
    Sentry.captureException(err);
    // TODO: Bing has to tell us what to put here
    toaster.error(
      `Error updating license: ${err?.response?.data?.Message || ''}`
    );

    yield put(dataActions.updateLicenseStatus({ status: oldStatus, index }));
  }
}

export function* userUpdateLicenseStatusWatcher() {
  yield takeLatest(
    dataActions.USER_UPDATE_LICENSE_STATUS,
    userUpdateLicenseStatusSaga
  );
}

function* userUpdatedLicenseRenewalStatusSaga(action) {
  const { oldStatus, renewalStatus, renewalId, index } = action.payload;
  yield put(
    dataActions.updateLicenseRenewalStatus({
      renewalStatus,
      index,
      inFlight: true,
    })
  );
  try {
    const { data } = yield strOracleApiSender.post(
      `/update_license_renewal_status/${renewalId}`,
      {
        status: renewalStatus,
      }
    );

    // Not setting inFlight to false bc entire object rewritten
    yield put(
      dataActions.serverReturnedUpdatedLicense({ license: data, index })
    );
  } catch (err) {
    console.error('Caught error:', err); // eslint-disable-line no-console
    Sentry.captureException(err);
    // TODO: Bing has to tell us what to put here
    toaster.error('Error updating license');

    yield put(
      dataActions.updateLicenseRenewalStatus({
        renewalStatus: oldStatus,
        index,
        inFlight: false,
      })
    );
  }
}

export function* userUpdatedLicenseRenewalStatusWatcher() {
  yield takeLatest(
    dataActions.USER_UPDATED_LICENSE_RENEWAL_STATUS,
    userUpdatedLicenseRenewalStatusSaga
  );
}

/**
 *  @param {string} action.payload.activeTab The active Property Details tab.
 *  @param {string} action.payload.propertyDeckardID Must be encoded as "retrieve" method of StrOracleApiSender will not perform the encoding.
 */
function* userClickedPropertyDetailsTabSaga(action) {
  const { activeTab, propertyDeckardID } = action.payload;

  // TODO: ES-724 - Maybe figure out if we need userSessionMgr still
  yield put(dashboardActions.updatePropertyDetailsTab({ activeTab }));

  /**
   * We will re-fetch data for the activity-feed and license tabs.
   * There will be a race condition between the notes and activity feed which may be an issue in
   * unstable connections
   * TODO: Add recovery logic to saga that handles /add_note that accounts for the race conditions
   */
  yield fetchGenericDetails(propertyDeckardID, activeTab);
}

export function* userClickedPropertyDetailsTabWatcher() {
  yield takeLatest(
    dataActions.USER_CLICKED_PROPERTY_DETAILS_TAB,
    userClickedPropertyDetailsTabSaga
  );
}

function* getKpiDataSaga({ hasRetried }) {
  // TODO: Add data "inFlight" functionality
  // ticket: https://deckardtech.monday.com/boards/741531296/pulses/810117429
  // yield put(dashboardActions.setKpiDataInFlight());
  try {
    const { data } = yield strOracleApiSender.get('/kpi');
    yield put(dataActions.serverReturnedKpiData({ kpiData: data }));
  } catch (err) {
    yield put(
      dashboardActions.setLoadingText({
        loadingText: LOADING_SCREEN_REFETCH_TEXT,
      })
    );
    if (!hasRetried) yield getKpiDataSaga({ hasRetried: Boolean(err) });
    else {
      Sentry.captureException(err);
      console.error('Caught error: ', err);
      throw err;
    }
  }
}

export function* fetchKpiDataWatcher() {
  yield takeLatest(dataActions.FETCH_KPI_DATA, getKpiDataSaga);
}

function* userUpdatedComplaintStatusInPropertyDetailsSaga(action) {
  const { complaint, status } = action.payload;

  const oldComplaintStatus = complaint.status;

  yield put(
    dataActions.updateServerReturnedPropertyComplaintsData({
      complaintId: complaint.complaintId,
      updatePayload: {
        inFlight: true,
        status,
      },
    })
  );

  try {
    const { data } = yield strOracleApiSender.post(
      `/update_complaint_status/${complaint.complaintId}`,
      { status }
    );

    yield put(
      dataActions.updateServerReturnedPropertyComplaintsData({
        complaintId: complaint.complaintId,
        updatePayload: {
          inFlight: false,
          status: data?.status,
        },
      })
    );
  } catch (err) {
    handleException(err, 'Error updating complaint status');

    yield put(
      dataActions.updateServerReturnedPropertyComplaintsData({
        complaintId: complaint.complaintId,
        updatePayload: {
          inFlight: false,
          status: oldComplaintStatus,
        },
      })
    );
  }
}

/**
 * Calls stroracle-api to update status of a given complaint
 * @param {number | string} action.complaintId Id of the complaint
 * @param {string} action.status New status to set on the complaint
 */
function* userUpdatedComplaintStatusSaga(action) {
  const { complaintId: rawComplaintId, status } = action.payload;
  // In case a grid row's node id is passes as the complaintId, it would be a string.
  // Convert it to compare with ids in the data
  const complaintId = parseInt(rawComplaintId, 10);

  const complaints = yield select(complaintsData);
  const complaintRowIndex = complaints.findIndex(
    (c) => c.complaintId === complaintId
  );

  if (complaintRowIndex < 0) {
    // Create an error for the sake of sending a stacktrace to Sentry
    const errorToReport = new Error(
      `Unable to find complaint index for id ${complaintId}`
    );
    handleException(errorToReport);
    return;
  }

  const oldStatus = complaints[complaintRowIndex]?.status;

  yield put(
    updateComplaintStatus({
      complaintRowIndex,
      status,
      inFlight: true,
    })
  );

  try {
    const { data } = yield strOracleApiSender.post(
      `/update_complaint_status/${complaintId}`,
      { status }
    );
    const updatedStatus = data?.status;

    yield put(
      updateComplaintStatus({
        complaintRowIndex,
        status: updatedStatus,
        inFlight: false,
      })
    );
  } catch (err) {
    handleException(err, 'Error updating complaint status');

    yield put(
      updateComplaintStatus({
        complaintRowIndex,
        status: oldStatus,
        inFlight: false,
      })
    );
  }
}

export function* userUpdatedComplaintStatusWatcher() {
  yield takeLatest(
    USER_UPDATE_COMPLAINT_STATUS,
    userUpdatedComplaintStatusSaga
  );
}

export function* userUpdatedComplaintStatusInPropertyDetailsWatcher() {
  yield takeLatest(
    dataActions.USER_UPDATE_COMPLAINT_STATUS_IN_PROPERTY_DETAILS,
    userUpdatedComplaintStatusInPropertyDetailsSaga
  );
}

function* userVerifiedListingSaga(action) {
  const { payload } = action;

  const {
    isVerifying,
    listingId,
    apn,
    address,
    suggestedAPN,
    suggestedSiteAddress,
    description,
    misc,
    listingDeckardId,
    propertyDeckardID,
  } = payload;

  const status = isVerifying ? 'verified' : 'disputed';
  const suggestedPropertyDetails = {
    suggested_apn: suggestedAPN,
    suggested_site_address: suggestedSiteAddress,
    comments: description,
  };

  const apiPayload = merge(
    {},
    { listing_id: listingId, status, misc, apn, site_address: address },
    isVerifying ? {} : suggestedPropertyDetails
  );

  try {
    yield strOracleApiSender.post(
      `/verify_listing/${listingDeckardId}`,
      apiPayload
    );

    yield fetchSingleGenericDetailsEndpoint({
      propertyDeckardID,
      path: '/property/',
      dataAction: dataActions.serverReturnedPropertyData,
      queryParams: { include_reviews: true },
      // This is required as it will fetch the updated data for the recently actioned upon listing
      headers: { 'Cache-Control': 'no-cache' },
    });
  } catch (err) {
    Sentry.captureException(err);
    toaster.error('Error updating the status of the listings.');
  }
}

export function* userVerifiedListingSagaWatcher() {
  yield takeLatest(dataActions.USER_VERIFIED_LISTING, userVerifiedListingSaga);
}
